import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LinkStatusView } from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';

const key = (locationId: string) => ['link-status', locationId] as const;

/** RL-453: a store's Link as it last reported, stale when old. */
export function useLinkStatus(locationId: string | null) {
  return useQuery({
    queryKey: key(locationId ?? ''),
    enabled: locationId !== null,
    refetchInterval: 15_000,
    queryFn: async (): Promise<LinkStatusView> => {
      const response = await apiClient.get<LinkStatusView>(
        `/connectors/locations/${locationId}/link-status`,
      );
      return response.data;
    },
  });
}

/** Ask the store's Link to retry one of its exports; it collects the request on its next report. */
export function useRetryLinkExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { locationId: string; exportId: string }) => {
      await apiClient.post(
        `/connectors/locations/${input.locationId}/link-status/exports/${input.exportId}/retry`,
      );
    },
    onSuccess: (_data, input) => queryClient.invalidateQueries({ queryKey: key(input.locationId) }),
  });
}

/**
 * RL-554: ask the store's Link to relay a source's cashiers and promotions
 * again in full; it starts on its next relay, after Back Office refuses its
 * next change as out of step.
 */
export function useRequestLinkRelayResend() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { locationId: string; sourceId: string }) => {
      await apiClient.post(
        `/connectors/locations/${input.locationId}/link-status/relay/${input.sourceId}/resend`,
      );
    },
    onSuccess: (_data, input) => queryClient.invalidateQueries({ queryKey: key(input.locationId) }),
  });
}
